// C:\Users\home\Desktop\coding_react\damoa-qr\src\pages\en\QrPage_tw.js
import React, { useState, useEffect, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../QrPage.css";

function App() {
  const [url, setUrl] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  // 選項（必要最少）
  const [size, setSize] = useState(224); // 128~512
  const [fgColor, setFgColor] = useState("#111827");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [includeMargin, setIncludeMargin] = useState(true);

  // CSR 基準 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "QR 碼產生器",
      description:
        "輸入網址即可產生 QR 碼，並可下載為 PNG 的 TooLoves QR 碼產生器。",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "QR, QR 碼, qrcode.react, QR PNG, QR 下載, TooLoves, 網路工具",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "TWD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 依深淺色模式同步瀏覽器/行動裝置導覽列顏色
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  const handleGenerate = (e) => {
    e?.preventDefault?.();
    const v = url.trim();
    if (!v) {
      alert("請輸入網站網址。");
      return;
    }
    setQrValue(v);
  };

  const handleDownload = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qrcode.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setUrl("");
    setQrValue("");
  };

  // 按下分享按鈕時開啟原生分享選單
  const handleShare = async () => {
    if (!qrValue) return;

    const isShareCanceled = (err) => {
      if (!err) return false;
      const name = err.name || "";
      const msg = (err.message || "").toLowerCase();
      return (
        name === "AbortError" ||
        name === "NotAllowedError" ||
        msg.includes("abort") ||
        msg.includes("cancel")
      );
    };

    const canvas = document.getElementById("qr-canvas");

    // 1) 嘗試檔案分享（若可行則以 PNG 檔）
    if (canvas && "toBlob" in canvas && typeof navigator?.canShare === "function") {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const file = new File([blob], "qrcode.png", { type: "image/png" });
        const shareDataWithFile = {
          files: [file],
          title: "QR 碼",
          text: qrValue,
        };

        if (navigator.canShare(shareDataWithFile)) {
          try {
            await navigator.share(shareDataWithFile);
            return; // 成功
          } catch (err) {
            if (isShareCanceled(err)) return; // 使用者取消
            // 其他錯誤則往下使用後備方案
          }
        }
      }
    }

    // 2) 不支援檔案分享：改用分享 URL
    if (navigator.share) {
      try {
        await navigator.share({
          title: "QR 碼",
          text: "請開啟以下連結。",
          url: qrValue,
        });
        return; // 成功
      } catch (err) {
        if (isShareCanceled(err)) return; // 使用者取消
        // 其他錯誤則往下使用後備方案
      }
    }

    // 3) 最終後備：將 URL 複製到剪貼簿
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(qrValue);
        alert("不支援分享選單，已將連結複製到剪貼簿。");
      } else {
        alert("此瀏覽器不支援分享。");
      }
    } catch {
      alert("此瀏覽器不支援分享。");
    }
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "zh-TW" }}>
        <title>QR 碼產生器 | TooLoves</title>
        <meta
          name="description"
          content="輸入網址即可產生 QR 碼，並可下載為 PNG。"
        />
        <meta
          name="keywords"
          content="QR, QR 碼, qrcode.react, QR PNG, QR 下載, TooLoves, 網路工具"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初始 theme-color，執行時以 useEffect 同步 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="zh_TW" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="QR 碼產生器 | TooLoves" />
        <meta
          property="og:description"
          content="從網站網址快速建立 QR 碼並以 PNG 儲存。"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-qr.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="QR 碼產生器 | TooLoves" />
        <meta
          name="twitter:description"
          content="從網站網址快速建立 QR 碼並以 PNG 儲存。"
        />
        <meta name="twitter:image" content="/og-qr.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 上方應用列 */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/tw" className="brand-link" aria-label="前往 TooLoves 中文（台灣）首頁">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "淺色模式" : "深色模式"}
          </button>
        </div>
      </header>

      {/* 內容 */}
      <main className="container">
        <section className="generator-card">
          <h2 className="card-title">QR 碼產生器</h2>
          <p className="card-subtitle">
            輸入網址並按下「產生」，即可在下方預覽與下載。
          </p>

          <form className="form" onSubmit={handleGenerate}>
            <label htmlFor="url" className="form-label sr-only">
              網站網址
            </label>
            <div className="input-row">
              <input
                id="url"
                type="text"
                placeholder="在此輸入網站網址"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
                inputMode="url"
              />
              <button type="submit" className="generate-btn">
                產生 QR
              </button>
            </div>

            <div className="options-row">
              <div className="field">
                <label className="form-label">大小</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
                <div className="hint">{size}px</div>
              </div>

              <div className="field">
                <label className="form-label">前景色</label>
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="form-label">背景色</label>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">包含邊距</label>
                <input
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                />
              </div>
            </div>
          </form>

          <div className="preview-card">
            {qrValue ? (
              <>
                <div className="qr-wrap">
                  <QRCodeCanvas
                    id="qr-canvas"
                    value={qrValue}
                    size={size}
                    bgColor={bgColor}
                    fgColor={fgColor}
                    includeMargin={includeMargin}
                  />
                </div>
                <p className="qr-url">{qrValue}</p>
                <div className="actions">
                  <button type="button" onClick={handleDownload}>
                    下載 PNG
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    重設
                  </button>

                  {/* 分享圖示按鈕（無文字） */}
                  <button
                    type="button"
                    className="btn-secondary icon-btn"
                    onClick={handleShare}
                    aria-label="分享"
                    title="分享"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {/* Feather/Lucide share-2 圖示 */}
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">
                請輸入網址後按「產生 QR」。
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法律資訊連結">
          <Link to="/tw/terms" className="footer-link">服務條款</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/tw/privacy" className="footer-link">隱私權政策</Link>
        </nav>
      </footer>
    </div>
  );
}

export default App;